import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Mail, Lock, Key, Loader2, ArrowLeft, ArrowRight, Binary, Phone } from 'lucide-react'
import { toast } from 'sonner'
import { sendCode, resetPassword } from '@/service/api/auth'
import { cn, hashPassword } from '@/lib/utils'
import { useConfigStore } from '@/store/config'
import {
  authCard,
  authCodeBtn,
  authFormInput,
  authIconBox,
  authInputIcon,
  authSubmitBtn,
} from '@/lib/ui-classes'
import { CaptchaGate } from '@/lib/captcha/CaptchaGate'

const getSchema = () => {
  return z.object({
    username: z.string().refine((val) => {
      const config = useConfigStore.getState().config
      const isEmail = config?.is_email ?? true
      const isPhone = config?.is_phone ?? false
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      const phoneRegex = /^1[3-9]\d{9}$/

      if (isEmail && isPhone) return emailRegex.test(val) || phoneRegex.test(val)
      if (isPhone) return phoneRegex.test(val)
      return emailRegex.test(val)
    }, { message: "请输入有效的账号格式" }),
    code: z.string().length(6, { message: "验证码必须是6位数字" }),
    newPassword: z.string().min(6, { message: "新密码至少需要6位" }),
  })
}

export default function ForgotPassword() {
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [sendingCode, setSendingCode] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const config = useConfigStore((state) => state.config)
  
  const isEmail = config?.is_email ?? true
  const isPhone = config?.is_phone ?? false
  const label = isEmail && isPhone ? '邮箱 / 手机号' : (isPhone ? '手机号' : '邮箱地址')
  const placeholder = isEmail && isPhone ? '请输入邮箱或手机号' : (isPhone ? '请输入手机号' : '请输入邮箱地址')
  const InputIcon = isPhone && !isEmail ? Phone : Mail
  
  const forgotPasswordSchema = getSchema()
  const { register, handleSubmit, formState: { errors }, getValues, trigger } = useForm<z.infer<typeof forgotPasswordSchema>>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  useEffect(() => {
    if (countdown <= 0) return

    const timer = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)

    return () => clearInterval(timer)
  }, [countdown])

  const sendVerificationCode = async (cap_token?: string) => {
    setSendingCode(true)

    try {
      const username = getValues('username')
      await sendCode({ username, type: 'reset', cap_token })
      toast.success('验证码已发送')
      setCountdown(60)
    } catch (error) {
      // Error handled by request helper
    } finally {
      setSendingCode(false)
    }
  }

  const onCaptchaSolved = async (token: string) => {
    await sendVerificationCode(token)
  }

  const onSendCodeBypass = async () => {
    await sendVerificationCode()
  }

  const onSubmit = async (data: z.infer<typeof forgotPasswordSchema>) => {
    setLoading(true)
    try {
      await resetPassword({
        ...data,
        newPassword: hashPassword(data.newPassword)
      })
      toast.success('密码重置成功，请使用新密码登录')
      router.push('/auth/login')
    } catch (error) {
      // Error handled by request helper
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-black relative overflow-hidden">
      <div className="absolute inset-0 w-full h-full">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#07F4F2]/15 rounded-full blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[#C1FAB8]/10 rounded-full blur-[120px]" />
          <div className="absolute inset-0 opacity-20 brightness-100 contrast-150 mix-blend-overlay" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
      </div>
      
      <div className="relative z-10 w-full max-w-md p-8 mx-4">
          <div className="flex flex-col items-center mb-8 text-center">
              <div className={cn(authIconBox, 'h-12 w-12 mb-4')}>
                  <Key className="h-6 w-6 text-[#0a0a0a]" />
              </div>
              <h1 className="text-3xl font-bold tracking-tight text-white mb-2">重置密码</h1>
              <p className="text-zinc-400">设置一个新的安全密码</p>
          </div>

          <div className={authCard}>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="space-y-2">
                      <Label htmlFor="username" className="text-zinc-300">{label}</Label>
                      <div className="relative group">
                          <InputIcon className={cn('absolute left-3 top-3 h-4 w-4', authInputIcon)} />
                          <Input
                              id="username"
                              placeholder={placeholder}
                              type={isPhone && !isEmail ? "tel" : "text"}
                              disabled={loading || sendingCode}
                              {...register('username')}
                              className={authFormInput}
                          />
                      </div>
                      {errors.username && (
                          <p className="text-xs text-red-500 mt-1">{errors.username.message}</p>
                      )}
                  </div>

                  <div className="space-y-2">
                      <Label htmlFor="code" className="text-zinc-300">验证码</Label>
                      <div className="flex gap-3">
                          <div className="relative group flex-1">
                              <Binary className={cn('absolute left-3 top-3 h-4 w-4', authInputIcon)} />
                              <Input
                                  id="code"
                                  placeholder="请输入验证码"
                                  maxLength={6}
                                  disabled={loading}
                                  {...register('code')}
                                  className={cn(authFormInput, 'font-mono tracking-widest')}
                              />
                          </div>
                          <CaptchaGate
                            scene="send_code"
                            mode="floating"
                            floatingPosition="bottom"
                            onBeforeFloating={async () => await trigger('username')}
                            onSolved={onCaptchaSolved}
                            onBypass={onSendCodeBypass}
                            onError={(message: string) => toast.error(message || '人机验证失败，请重试')}
                            trigger={
                              <button
                                type="button"
                                disabled={sendingCode || countdown > 0}
                                className={authCodeBtn}
                              >
                                {sendingCode ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-[#07F4F2]" />
                                ) : countdown > 0 ? (
                                  <span className="font-mono">{countdown}s</span>
                                ) : (
                                  "获取验证码"
                                )}
                              </button>
                            }
                          />
                      </div>
                      {errors.code && (
                          <p className="text-xs text-red-500 mt-1">{errors.code.message}</p>
                      )}
                  </div>

                  <div className="space-y-2">
                      <Label htmlFor="newPassword" className="text-zinc-300">新密码</Label>
                      <div className="relative group">
                          <Lock className={cn('absolute left-3 top-3 h-4 w-4', authInputIcon)} />
                          <Input
                              id="newPassword"
                              type="password"
                              placeholder="请输入新密码"
                              disabled={loading}
                              {...register('newPassword')}
                              className={authFormInput}
                          />
                      </div>
                      {errors.newPassword && (
                          <p className="text-xs text-red-500 mt-1">{errors.newPassword.message}</p>
                      )}
                  </div>

                  <button
                      type="submit"
                      disabled={loading}
                      className={authSubmitBtn}
                  >
                      {loading ? (
                          <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              重置中...
                          </>
                      ) : (
                          <>
                              重置密码
                              <ArrowRight className="h-4 w-4" />
                          </>
                      )}
                  </button>

                  <div className="text-center text-sm">
                      <Link href="/auth/login" className="text-zinc-400 hover:text-[#07F4F2] inline-flex items-center gap-1 transition-colors">
                          <ArrowLeft className="h-3 w-3" />
                          返回登录
                      </Link>
                  </div>
              </form>
          </div>
      </div>
    </div>
  )
}
